import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import Card from "../../../components/UI/card/Card";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { getUserId } from "../../../services/auth.service";
import { createNewShop } from "../../../services/shop.service";
import { STATES_OPTIONS } from "../../../constants/states";

// Form validation schema using Yup
const validationSchema = Yup.object({
  shopName: Yup.string().required("Shop name is required"),
  shopAddress: Yup.string().required("Shop address is required"),
  shopCategory: Yup.string().required("Shop category is required"),
  shopArea: Yup.string().required("Shop area is required"),
  shopCity: Yup.string().required("Shop city is required"),
  shopContactNumber: Yup.string()
    .required("Contact number is required")
    .matches(/^\d{10}$/, "Contact number must be 10 digits"),
  shopOwner: Yup.string().required("Shop owner is required"),
  shopState: Yup.string().required("Shop state is required"),
  shopSubCategory: Yup.string().required("Shop sub-category is required"),
});

const ShopForm: React.FC<any> = (props) => {
  const { t } = useTranslation();
  const [displayImage, setDisplayImage] = useState<any>(null);
  const navigate = useNavigate();
  return (
    <div className="w-100">
      <div className="row justify-content-center">
        <div className="col-lg-12 col-md-12 col-sm-12 w-100">
          <Card>
            <div className="product__edit p-4 w-100">
              <h3 className="mb-2">Shop</h3>

              {/* Image upload section */}
              <div className=" mb-2">
                <label htmlFor="pic" className="upload-icon mb-2">
                  <Icon
                    icon="akar-icons:cloud-upload"
                    style={{ fontSize: "40px", color: "#007bff" }}
                  />
                </label>
                <div className="d-none">
                  <input
                    className="form-control-file"
                    type="file"
                    id="pic"
                    name="pic"
                    onChange={(ev: any) =>
                      setDisplayImage(ev.currentTarget.files[0])
                    }
                    accept="image/png, image/jpeg"
                  />
                </div>
                <img
                  className="img-fluid rounded mt-3"
                  src={
                    displayImage
                      ? URL.createObjectURL(displayImage)
                      : "/images/store.png"
                  }
                  alt="product pic"
                  style={{ maxWidth: "200px" }}
                />
              </div>

              {/* Formik Form */}
              <Formik
                initialValues={{
                  shopName: props.shop?.shopName || "",
                  shopAddress: props.shop?.shopAddress || "",
                  shopCategory: props.shop?.shopCategory || "",
                  shopArea: props.shop?.shopArea || "",
                  shopCity: props.shop?.shopCity || "",
                  shopContactNumber: props.shop?.shopContactNumber || "",
                  shopOwner: props.shop?.shopOwner || "",
                  shopState: props.shop?.shopState || "",
                  shopSubCategory: props.shop?.shopSubCategory || "",
                  coolerSelf: props?.shop?.coolerSelf || false,
                  coolerCompany: props?.shop?.coolerCompany || false,
                }}
                validationSchema={validationSchema}
                onSubmit={async (values, { resetForm }) => {
                  // Handle form submission
                  console.log(values);
                  const payload = {
                    ...values,
                    createdBy: getUserId(),
                  };
                  const result = await createNewShop(payload);
                  if (result) {
                    resetForm();
                    navigate("/shops");
                  }
                }}
              >
                {({ errors, touched, values, setFieldValue }) => (
                  <Form>
                    <div className="gap-3 d-flex mb-3">
                      {/* Shop Name */}
                      <div className="form-group ps-0 col-sm-12 col-md-6">
                        <Field
                          id="shopName"
                          name="shopName"
                          type="text"
                          placeholder="Shop Name"
                          className={`form-control ${
                            errors.shopName && touched.shopName
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <ErrorMessage
                          name="shopName"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>

                      {/* Shop Address */}
                      <div className="form-group ps-0 pe-3 col-sm-12 col-md-6">
                        <Field
                          id="shopAddress"
                          name="shopAddress"
                          type="text"
                          placeholder="Shop Address"
                          className={`form-control ${
                            errors.shopAddress && touched.shopAddress
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <ErrorMessage
                          name="shopAddress"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                    </div>

                    {/* Shop Category */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="shopCategory"
                        name="shopCategory"
                        type="text"
                        placeholder="Shop Category"
                        className={`form-control ${
                          errors.shopCategory && touched.shopCategory
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="shopCategory"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Shop Area */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="shopArea"
                        name="shopArea"
                        type="text"
                        placeholder="Shop Area"
                        className={`form-control ${
                          errors.shopArea && touched.shopArea
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="shopArea"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    {/* Shop State */}
                    <div className="form-group p-0 mb-3">
                      <select
                        id="shopState"
                        name="shopState"
                        className={`form-control ${
                          errors.shopState && touched.shopState
                            ? "is-invalid"
                            : ""
                        }`}
                        value={values.shopState}
                        onChange={(ev) =>
                          setFieldValue("shopState", ev.target.value)
                        }
                      >
                        <option value={""} disabled>
                          Select State
                        </option>
                        {STATES_OPTIONS.map((item) => (
                          <option>{item.state}</option>
                        ))}
                      </select>
                      <ErrorMessage
                        name="shopState"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    {/* Shop City */}
                    <div className="form-group p-0 mb-3">
                      <select
                        id="shopCity"
                        name="shopCity"
                        value={values.shopCity}
                        className={`form-control ${
                          errors.shopCity && touched.shopCity
                            ? "is-invalid"
                            : ""
                        }`}
                        onChange={(ev) =>
                          setFieldValue("shopCity", ev.target.value)
                        }
                      >
                        <option value={""} disabled>
                          Select City
                        </option>
                        {STATES_OPTIONS.find(
                          (_: any) => _.state === values.shopState
                        )?.districts.map((item) => (
                          <option>{item}</option>
                        ))}
                      </select>
                      <ErrorMessage
                        name="shopCity"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Shop Contact Number */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="shopContactNumber"
                        name="shopContactNumber"
                        type="text"
                        placeholder="Shop Contact Number"
                        className={`form-control ${
                          errors.shopContactNumber && touched.shopContactNumber
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="shopContactNumber"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Shop Owner */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="shopOwner"
                        name="shopOwner"
                        type="text"
                        placeholder="Shop Owner"
                        className={`form-control ${
                          errors.shopOwner && touched.shopOwner
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="shopOwner"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    {/* Shop Sub-Category */}
                    <div className="form-group p-0 mb-3">
                      <Field
                        id="shopSubCategory"
                        name="shopSubCategory"
                        type="text"
                        placeholder="Shop Sub-Category"
                        className={`form-control ${
                          errors.shopSubCategory && touched.shopSubCategory
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="shopSubCategory"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>

                    <div className="d-flex gap-3 mb-3">
                      <div className="d-flex gap-3 p-0 mb-3">
                        <Field
                          id="coolerCompany"
                          name="coolerCompany"
                          type="checkbox"
                          placeholder="Shop Sub-Category"
                          className={`form-check ${
                            errors.coolerCompany && touched.coolerCompany
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <label htmlFor="coolerCompany">Cooper Other Company</label>
                      </div>
                      <div className="d-flex gap-3 p-0 mb-3">
                        <Field
                          id="coolerSelf"
                          name="coolerSelf"
                          type="checkbox"
                          placeholder="Shop Sub-Category"
                          className={`form-check ${
                            errors.coolerSelf && touched.coolerSelf
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <label htmlFor="coolerSelf">Cooper Self</label>
                      </div>
                    </div>

                    {/* Submit and Cancel buttons */}
                    <div className="d-flex justify-content-between mt-4">
                      <button type="submit" className="btn btn-primary px-4">
                        Submit
                      </button>
                      <Link to="/shops">
                        <button className="btn btn-secondary px-4">
                          {t("cancel")}
                        </button>
                      </Link>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ShopForm;
